import React from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import validateDNI from '~/utils/validateDNI';
import { getUserRequest } from '~/store/modules/user/actions';
import { editUser } from '~/services/api';

export default function FormConfig({ children, value: editValues, onSubmit }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleSubmit = async (values, { setSubmitting }) => {
    // Form data will be sent here if everything is valid
    try {
      await editUser({
        ...values,
      });
      toast.success(t('editAccountSuccess'));
    } catch (err) {
      toast.error(t('editAccountError'));
    } finally {
      onSubmit();
    }
    dispatch(getUserRequest());
    setSubmitting(false);
  };
  return (
    <Formik
      initialValues={{
        firstName: editValues.firstName,
        lastName: editValues.lastName,
        document: editValues.document,
        email: editValues.email,
        mediaName: editValues.mediaName,
        mediaType: editValues.mediaType,
        phoneNumber: editValues.phoneNumber,
      }}
      onSubmit={handleSubmit}
      validationSchema={Yup.object().shape({
        firstName: Yup.string()
          .min(3, t('mustHave3OrMoreLetters'))
          .max(30, t('mustHave30OrLessLetters'))
          .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/, t('mustContainOnlyLetters'))
          .required(t('nameIsRequiredValidationError')),
        lastName: Yup.string()
          .min(3, t('mustHave3OrMoreLetters'))
          .max(30, t('mustHave30OrLessLetters'))
          .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/, t('mustContainOnlyLetters'))
          .required(t('lastNameIsRequiredValidationError')),
        document: Yup.string()
          .test('dni-test', t('documentFormatInvalid'), value => {
            return validateDNI(value);
          })
          .required(t('dniIsRequiredValidationError')),
        email: Yup.string()
          .email(t('invalidEmail'))
          .required(t('emailIsRequiredValidationError')),
        mediaName: Yup.string()
          .min(2, t('mustHave3OrMoreLetters'))
          .max(15, t('mustHave15OrLessLetters'))
          .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/, t('mustContainOnlyLetters'))
          .required(t('mediaNameIsRequiredValidationError')),
        mediaType: Yup.string()
          .min(2, t('mustHave2OrMoreLetters'))
          .max(50, t('mustHave50OrLessLetters'))
          .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/, t('mustContainOnlyLetters')),
        phoneNumber: Yup.string().required(
          t('telephoneIsRequiredValidationError')
        ),
      })}
    >
      {children}
    </Formik>
  );
}

FormConfig.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  onSubmit: PropTypes.func,
  value: PropTypes.object,
};

FormConfig.defaultProps = {
  onSubmit: () => {},
  value: {},
};
